import { Button, Divider, FormSelectField, FormTextField, Grid, Switch, Typography } from 'ui';
import {
  ExternalSystemType,
  KeyType,
  useCreateTockApiKeyMutation,
  useGetAllTockApiKeyQuery,
  useUpdateTockApiKeyStatusMutation,
} from 'generated/graphql';
import { Form, Formik } from 'formik';
import { StoreType } from '@tock/shared/utils/connect';
import { css } from '@emotion/react';
import { gql } from '@apollo/client';
import { useActions } from 'utils/connect';
import { useSelector } from 'react-redux';
import ClowncarBodyV2 from 'components/ClowncarBodyV2';
import ClowncarHeaderV2 from 'components/ClowncarHeaderV2';
import React, { useState } from 'react';

// TODO pagination
// TODO filter by key type, status
// TODO search by contact email or description

gql`
  mutation CreateTockApiKey(
    $keyType: KeyType!
    $externalSystemType: ExternalSystemType
    $patronId: Int!
    $description: String!
    $contactEmail: String!
  ) {
    createTockApiKey(
      tockApiKeyGenerationRequest: {
        keyType: $keyType
        externalSystemType: $externalSystemType
        requestingPatronId: $patronId
        description: $description
        contactEmail: $contactEmail
      }
    )
  }
  mutation UpdateTockApiKeyStatus($uuid: String!, $isEnabled: Boolean!) {
    updateKeyStatus(keyUUID: $uuid, isEnabled: $isEnabled)
  }
`;

const FETCH_ALL_TOCK_API_KEY = gql`
  query GetAllTockApiKey {
    allApiKeys {
      uuid
      keyType
      externalSystemType
      status
      createdAt
      createdByPatron {
        firstName
        lastName
      }
      description
      contactEmail
    }
  }
`;

// Match the vertical rhythm of the text fields, which have no bottom margin and
// rely solely on the surrounding Grid `spacing`.
const selectFieldSpacing = css`
  && {
    margin-bottom: 0;
  }
`;

// External system types marked `[deprecated = true]` in external_system_type.proto.
const DEPRECATED_EXTERNAL_SYSTEM_TYPES: ReadonlySet<ExternalSystemType> = new Set([
  ExternalSystemType.Podium,
  ExternalSystemType.Volante,
  ExternalSystemType.Vinespring,
  ExternalSystemType.Winedirect,
]);

interface NewApiKeyFormFields {
  keyType: KeyType;
  externalSystemType: ExternalSystemType | '';
  description: string;
  contactEmail: string;
}

const TockApiKeyRoute: React.FunctionComponent = () => {
  const loggedInPatron = useSelector((state: StoreType) => state.app.patron);
  const { actions } = useActions();
  const { data } = useGetAllTockApiKeyQuery();
  const [issuedKeyJWT, setIssuedKeyJWT] = useState<string>();

  const [createKey] = useCreateTockApiKeyMutation();
  const [updateStatus] = useUpdateTockApiKeyStatusMutation();

  const handleSubmitKey = async ({
    keyType,
    externalSystemType,
    description,
    contactEmail,
  }: NewApiKeyFormFields) => {
    try {
      const { data } = await createKey({
        variables: {
          keyType,
          externalSystemType: externalSystemType || undefined,
          patronId: loggedInPatron.id,
          description,
          contactEmail,
        },
        refetchQueries: [
          {
            query: FETCH_ALL_TOCK_API_KEY,
          },
        ],
      });
      setIssuedKeyJWT(data?.createTockApiKey || undefined);
      actions.toast.showInfo('Tock Api Key created successfully');
    } catch {
      actions.toast.showError('Failed to create Tock Api Key');
    }
  };

  const handleUpdateKeyStatus = (uuid: string, isEnabled: boolean) => {
    return updateStatus({
      variables: { uuid, isEnabled },
      refetchQueries: [{ query: FETCH_ALL_TOCK_API_KEY }],
    });
  };

  return (
    <div className="Content">
      <ClowncarHeaderV2 heading="Tock Api Keys" />
      <div className="Main">
        <ClowncarBodyV2>
          <div>
            <Formik<NewApiKeyFormFields>
              initialValues={{
                keyType: KeyType.ApiKey,
                externalSystemType: '',
                description: '',
                contactEmail: '',
              }}
              onSubmit={handleSubmitKey}
            >
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="heading2Bold">Create a new Tock Api Key</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FormSelectField
                      css={selectFieldSpacing}
                      id="keyType"
                      name="keyType"
                      label="Key type"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ notched: true }}
                    >
                      {Object.values(KeyType).map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </FormSelectField>
                  </Grid>
                  <Grid item xs={8}>
                    <FormSelectField
                      css={selectFieldSpacing}
                      id="externalSystemType"
                      name="externalSystemType"
                      label="External system type"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ notched: true }}
                    >
                      <option value="">None</option>
                      {Object.values(ExternalSystemType)
                        .filter((value) => !DEPRECATED_EXTERNAL_SYSTEM_TYPES.has(value))
                        .map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                    </FormSelectField>
                  </Grid>
                  <Grid item xs={8}>
                    <FormTextField
                      id="description"
                      name="description"
                      label="Description"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <FormTextField
                      id="contactEmail"
                      name="contactEmail"
                      label="Contact email"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit">Submit</Button>
                  </Grid>
                </Grid>
              </Form>
            </Formik>
            <div>{issuedKeyJWT}</div>
          </div>
          <br />
          <Divider />
          <br />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="heading2Medium">Existing API Keys</Typography>
            </Grid>
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={2}>
                <Typography variant="body2Bold" role="columnheader">
                  Key type
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2Bold" role="columnheader">
                  External system type
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2Bold" role="columnheader">
                  Created by
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2Bold" role="columnheader">
                  Description
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2Bold" role="columnheader">
                  Contact email
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2Bold" role="columnheader">
                  Status
                </Typography>
              </Grid>

              {data?.allApiKeys.map((apiKey, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <Grid item xs={2}>
                      {apiKey.keyType}
                    </Grid>
                    <Grid item xs={2}>
                      {apiKey.externalSystemType ?? '—'}
                    </Grid>
                    <Grid item xs={2}>
                      {apiKey.createdByPatron.firstName} {apiKey.createdByPatron.lastName}
                    </Grid>
                    <Grid item xs={2}>
                      {apiKey.description}
                    </Grid>
                    <Grid item xs={2}>
                      {apiKey.contactEmail}
                    </Grid>
                    <Grid item xs={2}>
                      <Switch
                        checked={apiKey.status === 'ENABLED'}
                        onChange={(event) => {
                          handleUpdateKeyStatus(apiKey.uuid, event.target.checked);
                        }}
                      />
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
          </Grid>
        </ClowncarBodyV2>
      </div>
    </div>
  );
};

export default TockApiKeyRoute;
